import { useCallback } from 'react';
import PropTypes from 'prop-types';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import { all, includes } from 'ramda';
import { isNilOrEmpty } from 'ramda-extension';
import { useDispatch, useSelector } from 'react-redux';

import useFormData from '@gef-ui/form-core/hooks/state/useFormData';
import { validationDisplayTypes } from '@gef-ui/form-material/constants';
import TextField from '@gef-ui/form-material/TextField';
import { getLabelMessage } from '@gef-ui/localization/messages';

import DatePickerFullWidth from 'components/atoms/GefMui/DatePickerFullWidth';
import SectionHeader from 'components/atoms/GefMui/SectionHeader';
import { MaxLengths } from 'constants/forms';
import MODALS from 'constants/modals';
import { resetClientInfo, searchPartner } from 'features/views/taskCreatingPages/actions';
import {
	taskTypeBackoffice,
	taskTypeContactClient,
	taskTypeMobileScan,
	taskTypeSellerRequest,
} from 'features/views/taskCreatingPages/constants';
import {
	BACKOFFICE_FORM_NAME,
	CONTACT_CLIENT_FORM_NAME,
	FIELDS,
	MANUAL_LEAD_FORM_NAME,
	MOBILE_SCAN_FORM_NAME,
	SELLER_REQUEST_FORM_NAME,
} from 'features/views/taskCreatingPages/form';
import { getIsPartnerFieldsEditable } from 'features/views/taskCreatingPages/selectors';

import ClientSearchModal from '../modals/clientSearch/ClientSearchModal';

export const ClientInfo = ({ formName }) => {
	const dispatch = useDispatch();

	const fieldsEditable = useSelector(getIsPartnerFieldsEditable);
	const formData = useFormData();

	const handleSubmit = useCallback(() => {
		dispatch(searchPartner(formName));
	}, [dispatch, formName]);

	const handleReset = useCallback(() => {
		dispatch(resetClientInfo(formName));
	}, [dispatch, formName]);

	// pages have slightly different layouts
	const generateGridSizeByPage = useCallback(
		(fieldName) => {
			switch (fieldName) {
				case FIELDS.surname:
					return includes(formName, [SELLER_REQUEST_FORM_NAME, MANUAL_LEAD_FORM_NAME]) ? 4 : 3;
				case FIELDS.firstName:
					return includes(formName, [SELLER_REQUEST_FORM_NAME, MANUAL_LEAD_FORM_NAME]) ? 4 : 3;
				case FIELDS.businessName:
					return formName === SELLER_REQUEST_FORM_NAME ? 7 : 4;
				case FIELDS.municipality:
					return formName === MANUAL_LEAD_FORM_NAME ? 4 : 3;
				case FIELDS.email:
					return formName === MANUAL_LEAD_FORM_NAME ? 4 : 3;
			}
		},
		[formName],
	);

	const generateRequiredPropForZipCode = useCallback(() => {
		if (includes(formData?.taskType, [taskTypeBackoffice, taskTypeMobileScan, taskTypeSellerRequest])) {
			return true;
		} else {
			// excluding contactClient to get manualLead types only
			if (formData?.taskType !== taskTypeContactClient && formData?.radio === 'marketPlace') {
				return true;
			}
			return false;
		}
	}, [formData?.radio, formData?.taskType]);

	const generateRequiredPropForPhoneNumber = useCallback(() => {
		if (includes(formName, [SELLER_REQUEST_FORM_NAME, MANUAL_LEAD_FORM_NAME])) {
			return isNilOrEmpty(formData?.email);
		} else if (formName === CONTACT_CLIENT_FORM_NAME) {
			return true;
		} else {
			return false;
		}
	}, [formName, formData?.email]);

	return (
		<Paper sx={{ p: 3, borderRadius: 2 }}>
			<ClientSearchModal id={MODALS.client_search} />
			<Stack spacing={2}>
				<SectionHeader text={getLabelMessage('taskCreatingPages:clientInfo')} />
				<Grid container>
					<Grid size={12}>
						<Grid container spacing={2}>
							{formName === SELLER_REQUEST_FORM_NAME && (
								<Grid size={2}>
									<TextField
										size="small"
										label={getLabelMessage('taskCreatingPages:title')}
										fieldName={FIELDS.title}
										disabled={!fieldsEditable}
										slotProps={{
											htmlInput: {
												maxLength: MaxLengths.TITLE,
											},
										}}
									/>
								</Grid>
							)}
							<Grid size={generateGridSizeByPage(FIELDS.surname)}>
								<TextField
									size="small"
									label={getLabelMessage('taskCreatingPages:surname')}
									fieldName={FIELDS.surname}
									disabled={!fieldsEditable}
									required={
										includes(formData?.taskType, [
											taskTypeBackoffice,
											taskTypeMobileScan,
											taskTypeSellerRequest,
											taskTypeContactClient,
										]) && isNilOrEmpty(formData?.businessName)
									}
									validationDisplay={!formData?.surname ? validationDisplayTypes.HIDDEN : undefined}
									slotProps={{
										htmlInput: {
											maxLength: MaxLengths.SURNAME,
										},
									}}
								/>
							</Grid>
							<Grid size={generateGridSizeByPage(FIELDS.firstName)}>
								<TextField
									size="small"
									label={getLabelMessage('taskCreatingPages:firstName')}
									fieldName={FIELDS.firstName}
									disabled={!fieldsEditable}
									required={
										includes(formData?.taskType, [
											taskTypeBackoffice,
											taskTypeMobileScan,
											taskTypeSellerRequest,
											taskTypeContactClient,
										]) && isNilOrEmpty(formData?.businessName)
									}
									validationDisplay={!formData?.firstName ? validationDisplayTypes.HIDDEN : undefined}
									slotProps={{
										htmlInput: {
											maxLength: MaxLengths.NAME,
										},
									}}
								/>
							</Grid>
							{formName === SELLER_REQUEST_FORM_NAME && (
								<Grid size={2}>
									<DatePickerFullWidth
										label={getLabelMessage('taskCreatingPages:birthDate')}
										fieldName={FIELDS.birthDate}
										disabled={!fieldsEditable}
									/>
								</Grid>
							)}
							<Grid size={generateGridSizeByPage(FIELDS.businessName)}>
								<TextField
									size="small"
									label={getLabelMessage('taskCreatingPages:businessName')}
									fieldName={FIELDS.businessName}
									disabled={!fieldsEditable}
									required={
										includes(formData?.taskType, [
											taskTypeBackoffice,
											taskTypeMobileScan,
											taskTypeSellerRequest,
											taskTypeContactClient,
										]) && all(isNilOrEmpty)([formData?.firstName, formData?.surname])
									}
									validationDisplay={
										!formData?.businessName ? validationDisplayTypes.HIDDEN : undefined
									}
									slotProps={{
										htmlInput: {
											maxLength: MaxLengths.BUSINESS_NAME,
										},
									}}
								/>
							</Grid>
							{includes(formName, [
								BACKOFFICE_FORM_NAME,
								MOBILE_SCAN_FORM_NAME,
								CONTACT_CLIENT_FORM_NAME,
							]) && (
								<Grid size={2}>
									<TextField
										size="small"
										label={getLabelMessage('taskCreatingPages:personIn')}
										fieldName={FIELDS.personIn}
										disabled={!fieldsEditable}
										slotProps={{
											htmlInput: {
												maxLength: MaxLengths.BIRTH_NUMBER,
											},
										}}
									/>
								</Grid>
							)}
							{formName !== MANUAL_LEAD_FORM_NAME && (
								<>
									<Tooltip title={getLabelMessage('tooltips:taxNumber')}>
										<Grid size={2}>
											<TextField
												size="small"
												label={getLabelMessage('taskCreatingPages:businessIn')}
												fieldName={FIELDS.businessIn}
												disabled={!fieldsEditable}
												slotProps={{
													htmlInput: {
														maxLength: MaxLengths.TAX_NUMBER,
													},
												}}
											/>
										</Grid>
									</Tooltip>
									<Grid size={3}>
										<TextField
											size="small"
											label={getLabelMessage('taskCreatingPages:street')}
											fieldName={FIELDS.street}
											disabled={!fieldsEditable}
											slotProps={{
												htmlInput: {
													maxLength: MaxLengths.STREET,
												},
											}}
										/>
									</Grid>
									<Grid size={2}>
										<TextField
											size="small"
											label={getLabelMessage('taskCreatingPages:streetNo')}
											fieldName={FIELDS.streetNo}
											disabled={!fieldsEditable}
											slotProps={{
												htmlInput: {
													maxLength: MaxLengths.STREET_NO,
												},
											}}
										/>
									</Grid>
								</>
							)}

							<Grid size={generateGridSizeByPage(FIELDS.municipality)}>
								<TextField
									size="small"
									label={getLabelMessage('taskCreatingPages:municipality')}
									fieldName={FIELDS.municipality}
									disabled={!fieldsEditable}
									required={includes(formData?.taskType, [
										taskTypeBackoffice,
										taskTypeMobileScan,
										taskTypeSellerRequest,
									])}
									validationDisplay={
										!formData?.municipality ? validationDisplayTypes.HIDDEN : undefined
									}
									slotProps={{
										htmlInput: {
											maxLength: MaxLengths.MUNICIPALITY,
										},
									}}
								/>
							</Grid>
							<Tooltip title={getLabelMessage('tooltips:postalCode')}>
								<Grid size={2}>
									<TextField
										size="small"
										label={getLabelMessage('taskCreatingPages:zipCode')}
										fieldName={FIELDS.zipCode}
										disabled={!fieldsEditable}
										required={generateRequiredPropForZipCode()}
										validationDisplay={
											!formData?.zipCode ? validationDisplayTypes.HIDDEN : undefined
										}
										slotProps={{
											htmlInput: {
												maxLength: MaxLengths.ZIP_CODE,
											},
										}}
									/>
								</Grid>
							</Tooltip>
							{includes(formName, [
								SELLER_REQUEST_FORM_NAME,
								CONTACT_CLIENT_FORM_NAME,
								MANUAL_LEAD_FORM_NAME,
							]) && (
								<>
									<Tooltip title={getLabelMessage('tooltips:phone')}>
										<Grid size={2}>
											<TextField
												size="small"
												label={getLabelMessage('taskCreatingPages:phoneNumber')}
												fieldName={FIELDS.phoneNumber}
												required={generateRequiredPropForPhoneNumber()}
												validationDisplay={
													!formData?.phoneNumber ? validationDisplayTypes.HIDDEN : undefined
												}
												slotProps={{
													htmlInput: {
														maxLength: MaxLengths.PHONE,
													},
												}}
											/>
										</Grid>
									</Tooltip>
									<Tooltip title={getLabelMessage('tooltips:email')}>
										<Grid size={generateGridSizeByPage(FIELDS.email)}>
											<TextField
												size="small"
												label={getLabelMessage('taskCreatingPages:email')}
												fieldName={FIELDS.email}
												// in case of contactClient type - email field is not required
												required={
													formName !== CONTACT_CLIENT_FORM_NAME &&
													isNilOrEmpty(formData?.phoneNumber)
												}
												validationDisplay={
													!formData?.email ? validationDisplayTypes.HIDDEN : undefined
												}
												slotProps={{
													htmlInput: {
														maxLength: MaxLengths.EMAIL,
													},
												}}
											/>
										</Grid>
									</Tooltip>
								</>
							)}
							<Grid size={12}>
								<Grid
									container
									spacing={2}
									sx={{
										justifyContent: 'flex-end',
									}}
								>
									<Grid size={3}>
										<Button variant="outlined" onClick={handleReset} fullWidth>
											{getLabelMessage('common:resetButton')}
										</Button>
									</Grid>
									<Grid size={3}>
										<Button variant="contained" onClick={handleSubmit} fullWidth>
											{getLabelMessage('taskCreatingPages:clientInfoSearch')}
										</Button>
									</Grid>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				</Grid>
			</Stack>
		</Paper>
	);
};

ClientInfo.propTypes = {
	formName: PropTypes.string,
};

export default ClientInfo;
